import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import { runtimeConfig } from "src/services/runtimeConfig";
import { toast } from "sonner";
import { useUploadListContext } from "src/contexts/UploadListContext";
import {
  Grid,
  GridColumn as Column,
  GridCellProps,
  GridSortChangeEvent,
  GridPageChangeEvent,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import {
  CompositeFilterDescriptor,
  SortDescriptor,
  filterBy,
  orderBy,
} from "@progress/kendo-data-query";
import styles from "./NoCallHolidays.module.css";

// --- Types ---

interface SelectOption {
  value: string;
  text: string;
}

interface ConfigData {
  noCallHolidayConfigId: number;
  noCallHolidayConfigTitle: string;
  projId: string;
  projectName: string;
  campaignName: string;
  regionsUS: SelectOption[];
  regionsCA: SelectOption[];
}

interface HolidayRow {
  id: number;
  country: string;
  state: string;
  day: string;
  formattedDay: string;
  name: string;
  type: string;
  isFullDay: boolean;
  fromTime: string;
  toTime: string;
  isRecurring: boolean;
}

interface HolidayFormData {
  id: number;
  country: string;
  stateUS: string;
  stateCA: string;
  noCallHolidayName: string;
  isRecurring: boolean;
  isFullDay: boolean;
  day: string;
  fromTime: string;
  toTime: string;
  yearlyRecurType: number;
  yearlyRecurMonth: string;
  yrlyRecurDay: string;
  saturdayDoNotCallDay: string;
  sundayDoNotCallDay: string;
  yearlyDayToRecurOn: number;
  yearlyDayOfTheWeekToRecurOn: string;
  yearlyMonthToRecurOn: string;
}

const PAGE_SIZE_KEY = "noCallHolidays_pageSize";
const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZES = [10, 25, 50, 100];

const MONTHS = [
  { value: "1", text: "January" },
  { value: "2", text: "February" },
  { value: "3", text: "March" },
  { value: "4", text: "April" },
  { value: "5", text: "May" },
  { value: "6", text: "June" },
  { value: "7", text: "July" },
  { value: "8", text: "August" },
  { value: "9", text: "September" },
  { value: "10", text: "October" },
  { value: "11", text: "November" },
  { value: "12", text: "December" },
];

const DAYS_OF_WEEK = [
  { value: "0", text: "Sunday" },
  { value: "1", text: "Monday" },
  { value: "2", text: "Tuesday" },
  { value: "3", text: "Wednesday" },
  { value: "4", text: "Thursday" },
  { value: "5", text: "Friday" },
  { value: "6", text: "Saturday" },
];

const NTH_OPTIONS = [
  { value: 1, text: "First" },
  { value: 2, text: "Second" },
  { value: 3, text: "Third" },
  { value: 4, text: "Fourth" },
  { value: 99, text: "Last" },
];

const SATURDAY_OPTIONS = [
  { value: "Friday", text: "Do Not Call Day is Friday (default)" },
  { value: "Saturday", text: "Do Not Call Day is Saturday" },
];

const SUNDAY_OPTIONS = [
  { value: "Monday", text: "Do Not Call Day is Monday (default)" },
  { value: "Sunday", text: "Do Not Call Day is Sunday" },
];

// Helper to get days in a month
function getDaysInMonth(year: number, month: number): number[] {
  const count = new Date(year, month, 0).getDate();
  return Array.from({ length: count }, (_, i) => i + 1);
}

const NoCallHolidays: React.FC = () => {
  const navigate = useNavigate();
  const { projectId, campaignId } = useUploadListContext();

  // Config state
  const [config, setConfig] = useState<ConfigData | null>(null);
  const [configType, setConfigType] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  // Grid state
  const [holidays, setHolidays] = useState<HolidayRow[]>([]);
  const [sort, setSort] = useState<SortDescriptor[]>([
    { field: "day", dir: "asc" },
  ]);
  const [filter, setFilter] = useState<CompositeFilterDescriptor | undefined>();
  const [page, setPage] = useState({
    skip: 0,
    take:
      Number(localStorage.getItem(PAGE_SIZE_KEY)) || DEFAULT_PAGE_SIZE,
  });

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState<HolidayFormData>(getDefaultFormData());
  const [daysInSelectedMonth, setDaysInSelectedMonth] = useState<number[]>(
    getDaysInMonth(new Date().getFullYear(), new Date().getMonth() + 1),
  );

  // Load config on mount
  useEffect(() => {
    if (projectId && campaignId) {
      loadConfig();
    }
  }, [projectId, campaignId]);

  // Load holidays when config, configType, or campaignId changes
  useEffect(() => {
    if (config) {
      loadHolidays();
    }
  }, [config, configType, campaignId]);

  const loadConfig = async () => {
    try {
      const data = await callApi(
        `main/nocallholidays/getconfig?campaignId=${campaignId}`,
        HttpMethod.GET,
      );
      setConfig(data);
      setConfigType(data.noCallHolidayConfigId);
    } catch (err: any) {
      toast.error(err.message || "Failed to load configuration");
    } finally {
      setIsLoading(false);
    }
  };

  const loadHolidays = async () => {
    if (!config) return;
    try {
      const data = await callApi(
        `main/nocallholidays/getholidays?campaignId=${campaignId}&configType=${configType}`,
        HttpMethod.GET,
      );
      setHolidays(data.records ?? []);
    } catch (err: any) {
      toast.error(err.message || "Failed to load holidays");
    }
  };

  // Save config selection
  const handleConfigChange = async (newConfig: number) => {
    if (!config) return;
    setConfigType(newConfig);
    try {
      await callApi(`main/nocallholidays/saveconfig?campaignId=${campaignId}`, HttpMethod.POST, {
        config: newConfig,
      });
      toast.success("Configuration saved");
    } catch (err: any) {
      toast.error(err.message || "Failed to save configuration");
    }
  };

  // CSV export
  const handleExportCsv = () => {
    if (!config) return;
    const baseUrl = runtimeConfig.apiUrl;
    window.open(
      `${baseUrl}/main/nocallholidays/exportcsv?campaignId=${campaignId}&configType=${configType}`,
      "_blank",
    );
  };

  // Open modal for add or edit
  const openModal = async (holidayId: number = 0) => {
    try {
      const data = await callApi(
        `main/nocallholidays/getholiday?holidayId=${holidayId}&campaignId=${campaignId}`,
        HttpMethod.GET,
      );
      setForm(data);
      // Update days for the selected month
      const month = parseInt(data.yearlyRecurMonth) || new Date().getMonth() + 1;
      setDaysInSelectedMonth(getDaysInMonth(new Date().getFullYear(), month));
      setShowModal(true);
    } catch (err: any) {
      toast.error(err.message || "Failed to load holiday data");
    }
  };

  // Delete holiday
  const handleDelete = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this holiday?"))
      return;
    try {
      await callApi(
        `main/nocallholidays/deleteholiday?id=${id}&campaignId=${campaignId}`,
        HttpMethod.DELETE,
      );
      toast.success("Holiday deleted");
      loadHolidays();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete holiday");
    }
  };

  // Save holiday form
  const handleSaveHoliday = async () => {
    // Validation
    if (!form.noCallHolidayName.trim()) {
      toast.error("Holiday name is required");
      return;
    }
    const state = form.country === "US" ? form.stateUS : form.stateCA;
    if (!state) {
      toast.error("State/Province is required");
      return;
    }
    if (!form.isRecurring) {
      const dayDate = new Date(form.day);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (dayDate < today) {
        toast.error("Date cannot be in the past");
        return;
      }
    }
    if (!form.isFullDay && form.fromTime >= form.toTime) {
      toast.error("From Time must be before To Time");
      return;
    }

    setIsSubmitting(true);
    try {
      // Send times as local DateTime strings (no UTC conversion).
      // Backend uses DateTime.Now (local time), never UTC.
      const today = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      const [fromH, fromM] = form.fromTime.split(":").map(Number);
      const [toH, toM] = form.toTime.split(":").map(Number);
      const dateStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
      const fromTimeLocal = `${dateStr}T${pad(fromH)}:${pad(fromM)}:00`;
      const toTimeLocal = `${dateStr}T${pad(toH)}:${pad(toM)}:00`;
      const dayLocal = form.day + "T00:00:00";

      const result = await callApi(
        `main/nocallholidays/saveholiday?campaignId=${campaignId}`,
        HttpMethod.POST,
        {
          ...form,
          fromTime: fromTimeLocal,
          toTime: toTimeLocal,
          day: dayLocal,
        },
      );
      if (result.result) {
        toast.success("Holiday saved successfully");
        setShowModal(false);
        loadHolidays();
      } else {
        toast.error(result.message || "Failed to save holiday");
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save holiday");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form field updater
  const updateForm = (field: keyof HolidayFormData, value: any) => {
    setForm((prev) => ({ ...prev, [field]: value }));

    // Update days when month changes
    if (field === "yearlyRecurMonth") {
      const month = parseInt(value) || 1;
      setDaysInSelectedMonth(getDaysInMonth(new Date().getFullYear(), month));
    }
  };

  // Grid data processing
  const processedData = useMemo(() => {
    let data = [...holidays];
    if (filter) data = filterBy(data, filter);
    if (sort.length > 0) data = orderBy(data, sort);
    return data;
  }, [holidays, sort, filter]);

  const pagedData = useMemo(() => {
    return processedData.slice(page.skip, page.skip + page.take);
  }, [processedData, page]);

  const handleSortChange = (e: GridSortChangeEvent) => setSort(e.sort);
  const handleFilterChange = (e: GridFilterChangeEvent) => setFilter(e.filter);
  const handlePageChange = (e: GridPageChangeEvent) => {
    setPage(e.page);
    localStorage.setItem(PAGE_SIZE_KEY, String(e.page.take));
  };

  // Custom cell for Date column — renders the time portion in bold
  // The API returns formattedDay with <b> tags; we parse and render properly
  const DateCell = useCallback((props: GridCellProps) => {
    const raw: string = props.dataItem.formattedDay ?? "";
    const match = raw.match(/^(.*?)\s*<b>(.*?)<\/b>\s*$/);
    if (match) {
      return (
        <td>
          {match[1]} <b>{match[2]}</b>
        </td>
      );
    }
    return <td>{raw}</td>;
  }, []);

  // Custom cell for Actions column
  const ActionsCell = useCallback(
    (props: GridCellProps) => {
      const { dataItem } = props;
      const isCustom = dataItem.type === "Custom";
      return (
        <td>
          {isCustom && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => openModal(dataItem.id)}
              >
                Edit
              </button>
              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => handleDelete(dataItem.id)}
              >
                Delete
              </button>
            </div>
          )}
        </td>
      );
    },
    [],
  );

  // Custom cell for Type column (badge)
  const TypeCell = useCallback((props: GridCellProps) => {
    const type = props.dataItem.type;
    const badgeClass =
      type === "Custom" ? "badge bg-info" : "badge bg-secondary";
    return (
      <td>
        <span className={badgeClass}>{type}</span>
      </td>
    );
  }, []);

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className="d-flex justify-content-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className={styles.container}>
        <div className="alert alert-warning">
          Unable to load No Call Holiday configuration. Please ensure a valid
          project and campaign are selected.
        </div>
      </div>
    );
  }

  const showCustomActions =
    configType === 1 || configType === 2; // DefaultPlusCustom or CustomOnly

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <h5 className="mb-0">No Call Holidays</h5>
        <button
          className="btn btn-secondary btn-sm"
          onClick={() => navigate(`/main/project-settings/${campaignId}`)}
        >
          Back to Project Settings
        </button>
      </div>

      {/* Config radio buttons */}
      <div className={styles.configSection}>
        <strong>Configuration:</strong>
        <label>
          <input
            type="radio"
            name="noCallHolidayConfig"
            value={0}
            checked={configType === 0}
            onChange={() => handleConfigChange(0)}
          />
          Default Holidays
        </label>
        <label>
          <input
            type="radio"
            name="noCallHolidayConfig"
            value={1}
            checked={configType === 1}
            onChange={() => handleConfigChange(1)}
          />
          Default + Custom Holidays
        </label>
        <label>
          <input
            type="radio"
            name="noCallHolidayConfig"
            value={2}
            checked={configType === 2}
            onChange={() => handleConfigChange(2)}
          />
          Custom Holidays Only
        </label>
      </div>

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <span className="text-muted">
          {configType === 1
            ? "Default and Custom No Call Holidays"
            : configType === 2
              ? "Custom No Call Holidays"
              : "Default No Call Holidays"}
        </span>
        <div className={styles.toolbarRight}>
          {showCustomActions && (
            <button
              className="btn btn-primary btn-sm"
              onClick={() => openModal(0)}
            >
              Add Custom Holiday
            </button>
          )}
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={handleExportCsv}
            disabled={holidays.length === 0}
          >
            Export CSV
          </button>
        </div>
      </div>

      {/* Kendo Grid */}
      <Grid
        data={pagedData}
        total={processedData.length}
        dataItemKey="id"
        sortable={{ mode: "multiple", allowUnsort: true }}
        sort={sort}
        onSortChange={handleSortChange}
        filterable={true}
        filter={filter}
        onFilterChange={handleFilterChange}
        pageable={{ pageSizes: PAGE_SIZES }}
        skip={page.skip}
        take={page.take}
        onPageChange={handlePageChange}
        style={{ height: "calc(100vh - 310px)" }}
      >
        <Column field="country" title="Country" width={100} />
        <Column field="state" title="Region" width={200} />
        <Column field="name" title="Holiday Name" width={220} />
        <Column
          field="formattedDay"
          title="Next Date"
          width={280}
          cells={{ data: DateCell }}
        />
        <Column
          field="type"
          title="Type"
          width={110}
          cells={{ data: TypeCell }}
        />
        {showCustomActions && (
          <Column
            title="Actions"
            width={160}
            filterable={false}
            sortable={false}
            cells={{ data: ActionsCell }}
          />
        )}
      </Grid>

      {/* Add/Edit Modal */}
      {showModal && (
        <div
          className="modal d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {form.id > 0
                    ? "Edit Custom Holiday"
                    : "Add Custom Holiday"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                />
              </div>
              <div className="modal-body">
                {/* Country & State */}
                <div className={styles.formRow}>
                  <div className={styles.formGroup}>
                    <label>Country</label>
                    <select
                      className="form-select"
                      value={form.country}
                      onChange={(e) => updateForm("country", e.target.value)}
                    >
                      <option value="US">United States</option>
                      <option value="CA">Canada</option>
                    </select>
                  </div>
                  <div className={styles.formGroup}>
                    <label>State / Province</label>
                    {form.country === "US" ? (
                      <select
                        className="form-select"
                        value={form.stateUS}
                        onChange={(e) => updateForm("stateUS", e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {config.regionsUS.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.text}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <select
                        className="form-select"
                        value={form.stateCA}
                        onChange={(e) => updateForm("stateCA", e.target.value)}
                      >
                        <option value="">-- Select --</option>
                        {config.regionsCA.map((r) => (
                          <option key={r.value} value={r.value}>
                            {r.text}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>

                {/* Name */}
                <div className={styles.formGroup}>
                  <label>Holiday Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={form.noCallHolidayName}
                    onChange={(e) =>
                      updateForm("noCallHolidayName", e.target.value)
                    }
                    maxLength={100}
                  />
                </div>

                {/* Occurrence */}
                <div className={styles.formGroup}>
                  <label>Occurrence</label>
                  <div className="d-flex gap-3">
                    <label className="d-flex align-items-center gap-1">
                      <input
                        type="radio"
                        name="isRecurring"
                        checked={form.isRecurring}
                        onChange={() => updateForm("isRecurring", true)}
                      />
                      Recurs Yearly
                    </label>
                    <label className="d-flex align-items-center gap-1">
                      <input
                        type="radio"
                        name="isRecurring"
                        checked={!form.isRecurring}
                        onChange={() => updateForm("isRecurring", false)}
                      />
                      One Time Holiday
                    </label>
                  </div>
                </div>

                {/* One-time date picker */}
                {!form.isRecurring && (
                  <div className={styles.formGroup}>
                    <label>Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.day}
                      onChange={(e) => updateForm("day", e.target.value)}
                      style={{ maxWidth: 220 }}
                    />
                    <small className={styles.warningText}>
                      Warning: One-time holidays do not recur. Select "Recurs
                      Yearly" if this is a yearly recurring holiday.
                    </small>
                  </div>
                )}

                {/* Full day / Specific time */}
                <div className={styles.formGroup}>
                  <label>Duration</label>
                  <div className="d-flex gap-3 mb-2">
                    <label className="d-flex align-items-center gap-1">
                      <input
                        type="radio"
                        name="isFullDay"
                        checked={form.isFullDay}
                        onChange={() => updateForm("isFullDay", true)}
                      />
                      Entire Day
                    </label>
                    <label className="d-flex align-items-center gap-1">
                      <input
                        type="radio"
                        name="isFullDay"
                        checked={!form.isFullDay}
                        onChange={() => updateForm("isFullDay", false)}
                      />
                      Specific Time
                    </label>
                  </div>
                  {!form.isFullDay && (
                    <div className={styles.timeRow}>
                      <strong>From</strong>
                      <input
                        type="time"
                        className="form-control"
                        value={form.fromTime}
                        onChange={(e) => updateForm("fromTime", e.target.value)}
                        style={{ maxWidth: 150 }}
                      />
                      <strong>To</strong>
                      <input
                        type="time"
                        className="form-control"
                        value={form.toTime}
                        onChange={(e) => updateForm("toTime", e.target.value)}
                        style={{ maxWidth: 150 }}
                      />
                    </div>
                  )}
                </div>

                {/* Recurring options */}
                {form.isRecurring && (
                  <div>

                    {/* Type 1: Every Month/Day */}
                    <div className={styles.recurOption}>
                      <input
                        type="radio"
                        name="yearlyRecurType"
                        checked={form.yearlyRecurType === 1}
                        onChange={() => updateForm("yearlyRecurType", 1)}
                      />
                      <span>Every</span>
                      <select
                        className="form-select form-select-sm"
                        value={form.yearlyRecurMonth}
                        onChange={(e) =>
                          updateForm("yearlyRecurMonth", e.target.value)
                        }
                        disabled={form.yearlyRecurType !== 1}
                        style={{ maxWidth: 140 }}
                      >
                        {MONTHS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.text}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm"
                        value={form.yrlyRecurDay}
                        onChange={(e) =>
                          updateForm("yrlyRecurDay", e.target.value)
                        }
                        disabled={form.yearlyRecurType !== 1}
                        style={{ maxWidth: 80 }}
                      >
                        {daysInSelectedMonth.map((d) => (
                          <option key={d} value={d}>
                            {d}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Weekend rules (only for Type 1) */}
                    {form.yearlyRecurType === 1 && (
                      <div className={styles.weekendRules}>
                        <div className={styles.formGroup}>
                          <label>If day is a Saturday</label>
                          <select
                            className="form-select form-select-sm"
                            value={form.saturdayDoNotCallDay}
                            onChange={(e) =>
                              updateForm(
                                "saturdayDoNotCallDay",
                                e.target.value,
                              )
                            }
                          >
                            {SATURDAY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.text}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={styles.formGroup}>
                          <label>If day is a Sunday</label>
                          <select
                            className="form-select form-select-sm"
                            value={form.sundayDoNotCallDay}
                            onChange={(e) =>
                              updateForm("sundayDoNotCallDay", e.target.value)
                            }
                          >
                            {SUNDAY_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.text}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}

                    {/* Type 4+: Every Nth Weekday of Month */}
                    <div className={styles.recurOption}>
                      <input
                        type="radio"
                        name="yearlyRecurType"
                        checked={
                          form.yearlyRecurType !== 1 &&
                          form.yearlyRecurType !== 2 &&
                          form.yearlyRecurType !== 3
                        }
                        onChange={() => updateForm("yearlyRecurType", 4)}
                      />
                      <span>Every</span>
                      <select
                        className="form-select form-select-sm"
                        value={form.yearlyDayToRecurOn}
                        onChange={(e) =>
                          updateForm(
                            "yearlyDayToRecurOn",
                            parseInt(e.target.value),
                          )
                        }
                        disabled={
                          form.yearlyRecurType === 1 ||
                          form.yearlyRecurType === 2 ||
                          form.yearlyRecurType === 3
                        }
                        style={{ maxWidth: 100 }}
                      >
                        {NTH_OPTIONS.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.text}
                          </option>
                        ))}
                      </select>
                      <select
                        className="form-select form-select-sm"
                        value={form.yearlyDayOfTheWeekToRecurOn}
                        onChange={(e) =>
                          updateForm(
                            "yearlyDayOfTheWeekToRecurOn",
                            e.target.value,
                          )
                        }
                        disabled={
                          form.yearlyRecurType === 1 ||
                          form.yearlyRecurType === 2 ||
                          form.yearlyRecurType === 3
                        }
                        style={{ maxWidth: 130 }}
                      >
                        {DAYS_OF_WEEK.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.text}
                          </option>
                        ))}
                      </select>
                      <span>of</span>
                      <select
                        className="form-select form-select-sm"
                        value={form.yearlyMonthToRecurOn}
                        onChange={(e) =>
                          updateForm("yearlyMonthToRecurOn", e.target.value)
                        }
                        disabled={
                          form.yearlyRecurType === 1 ||
                          form.yearlyRecurType === 2 ||
                          form.yearlyRecurType === 3
                        }
                        style={{ maxWidth: 140 }}
                      >
                        {MONTHS.map((m) => (
                          <option key={m.value} value={m.value}>
                            {m.text}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Type 2: Mardi Gras (only show for US) */}
                    {form.country === "US" && (
                      <div className={styles.recurOption}>
                        <input
                          type="radio"
                          name="yearlyRecurType"
                          checked={form.yearlyRecurType === 2}
                          onChange={() => updateForm("yearlyRecurType", 2)}
                        />
                        <span>Mardi Gras</span>
                      </div>
                    )}

                    {/* Type 3: Good Friday */}
                    <div className={styles.recurOption}>
                      <input
                        type="radio"
                        name="yearlyRecurType"
                        checked={form.yearlyRecurType === 3}
                        onChange={() => updateForm("yearlyRecurType", 3)}
                      />
                      <span>Good Friday</span>
                    </div>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleSaveHoliday}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

function getDefaultFormData(): HolidayFormData {
  const now = new Date();
  return {
    id: 0,
    country: "US",
    stateUS: "",
    stateCA: "",
    noCallHolidayName: "",
    isRecurring: true,
    isFullDay: true,
    day: now.toISOString().split("T")[0],
    fromTime: "06:00",
    toTime: "23:00",
    yearlyRecurType: 1,
    yearlyRecurMonth: String(now.getMonth() + 1),
    yrlyRecurDay: "1",
    saturdayDoNotCallDay: "Friday",
    sundayDoNotCallDay: "Monday",
    yearlyDayToRecurOn: 1,
    yearlyDayOfTheWeekToRecurOn: "1",
    yearlyMonthToRecurOn: String(now.getMonth() + 1),
  };
}

export default NoCallHolidays;
